export interface PianoConfig {
  whiteKeyWidth: number;
  whiteKeyHeight: number;
  numOctaves: number;
  octaveOffset: number;
  userPianoHeight: number;
  pressedKeyColor: string;
}

export const getWhiteKeyDimensions = (
  numOctaves: number,
): { width: number; height: number } => {
  switch (numOctaves) {
    case 1: return { width: 22, height: 82 };
    case 3: return { width: 49.4, height: 190 };
    case 5: return { width: 30, height: 135 };
    case 6: return { width: 25.2, height: 112 };
    default: return { width: 25.2, height: 112 };
  }
};

export const hexToRgba = (hex: string, alpha: number): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return `rgba(0,255,0,${alpha})`;
  return `rgba(${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)},${alpha})`;
};

/* Maps MIDI note → X centre in the 1080-px master canvas */
export const buildNoteToXMap = (config: PianoConfig): Record<number, number> => {
  const { whiteKeyWidth, octaveOffset } = config;
  const blackKeyWidth = whiteKeyWidth / 2;
  const whiteNotesMapper = [
    0, 2, 4, 5, 7, 9, 11, 12, 14, 16, 17, 19, 21, 23, 24, 26, 28, 29, 31, 33,
    35, 36, 38, 40, 41, 43, 45, 47, 48, 50, 52, 53, 55, 57, 59, 60, 62, 64, 65,
    67, 69, 71, 72, 74, 76, 77, 79, 81, 83, 84, 86, 88,
  ];
  const blackNotesMapper = [
    1, 3, 6, 8, 10, 13, 15, 18, 20, 22, 25, 27, 30, 32, 34, 37, 39, 42, 44, 46,
    49, 51, 54, 56, 58, 61, 63, 66, 68, 70, 73, 75, 78, 80, 82, 85, 87, 90, 92,
    94, 97, 99, 102, 104, 106, 109, 111, 114, 116, 118, 121, 123, 126,
  ];
  const noteNames = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B'];
  const blackKeyOffsets: Record<string, number> = { 'C#': -2.2, 'D#': 2.2, 'F#': -2.2, 'A#': 2.2 };
  const blackKeyPattern = [1, 1, 0, 1, 1, 1, 0];
  const shift = (octaveOffset - 1) * 12;
  const noteToX: Record<number, number> = {};

  for (let i = 0; i < 52; i++) {
    const midiNote = whiteNotesMapper[i] + shift;
    if (midiNote >= 0 && midiNote <= 127) {
      noteToX[midiNote] = i * whiteKeyWidth + whiteKeyWidth / 2;
    }
  }
  let blackNoteCounter = 0;
  for (let i = 0; i < 52 && blackNoteCounter < blackNotesMapper.length; i++) {
    if (blackKeyPattern[i % 7]) {
      const midiNote = blackNotesMapper[blackNoteCounter] + shift;
      if (midiNote >= 0 && midiNote <= 127) {
        const noteName = noteNames[midiNote % 12];
        const offset = blackKeyOffsets[noteName] || 0;
        noteToX[midiNote] = (i + 1) * whiteKeyWidth - blackKeyWidth / 2 + offset;
      }
      blackNoteCounter++;
    }
  }
  return noteToX;
};

/* ── Master canvas compositing ────────────────────────────────────────────── */

export const MASTER_CANVAS_WIDTH = 1080;
export const MASTER_CANVAS_HEIGHT = 1350;

export interface MasterCanvasLayoutInput {
  /** `HTMLVideoElement.videoWidth` — 0 until the first frame has decoded. */
  videoIntrinsicWidth: number;
  videoIntrinsicHeight: number;
  /** Drawing-buffer height of the offscreen piano canvas. */
  pianoCanvasHeight: number;
  userPianoHeight: number;
  canvasWidth?: number;
  canvasHeight?: number;
}

export interface MasterCanvasLayout {
  video: { x: number; y: number; width: number; height: number };
  piano: { x: number; y: number; width: number; height: number };
}

/**
 * Where the camera frame and the piano overlay land on the 1080x1350 export
 * canvas. Returns `null` when the video has no usable intrinsic size yet:
 * feeding non-finite coordinates to `drawImage` is a silent no-op, which shows
 * up as a stalled frame rather than an error.
 */
export const computeMasterCanvasLayout = (
  input: MasterCanvasLayoutInput,
): MasterCanvasLayout | null => {
  const {
    videoIntrinsicWidth,
    videoIntrinsicHeight,
    pianoCanvasHeight,
    userPianoHeight,
    canvasWidth = MASTER_CANVAS_WIDTH,
    canvasHeight = MASTER_CANVAS_HEIGHT,
  } = input;

  if (
    !Number.isFinite(videoIntrinsicWidth) ||
    !Number.isFinite(videoIntrinsicHeight) ||
    videoIntrinsicWidth <= 0 ||
    videoIntrinsicHeight <= 0
  ) {
    return null;
  }

  // The camera fills the canvas height and is centred horizontally.
  const drawHeight = canvasHeight;
  const drawWidth = canvasHeight * (videoIntrinsicWidth / videoIntrinsicHeight);

  // The piano sits in whatever space is left below the video, lifted by the
  // user's chosen offset. The offset is doubled because the on-screen preview
  // is half the size of the export canvas.
  const remainingSpaceBelow = canvasHeight - drawHeight;
  const pianoYPosition =
    drawHeight + (remainingSpaceBelow - pianoCanvasHeight) / 2 - 700;

  return {
    video: {
      x: (canvasWidth - drawWidth) / 2,
      y: 0,
      width: drawWidth,
      height: drawHeight,
    },
    piano: {
      x: 0,
      y: pianoYPosition + 25 - userPianoHeight * 2,
      width: canvasWidth,
      height: pianoCanvasHeight + 50,
    },
  };
};

/**
 * Y coordinate of the top edge of the keys on the master canvas. The effects
 * pass anchors its glow line and particles here, and must agree with what
 * `computeMasterCanvasLayout` actually drew.
 */
export const computePianoTopY = (userPianoHeight: number): number =>
  -userPianoHeight * 2;
